const BASE_URL = (window.COSMA_CONFIG && window.COSMA_CONFIG.apiUrl) || process.env.REACT_APP_API;

// Login Section
// No REST login/register endpoints exist on dementia at all — auth is
// Cognito-only (utils/cognito.js). LOGIN_API had zero callers even before
// this migration. DOC_REGISTER_API/PATIENT_REGISTER_API's only callers were
// an unreachable form.type branch (dead code, confirmed — the live
// registration UI never sets type to "doctor" or "patient"), so both are
// dropped along with that branch rather than migrated.
export const REGISTER_API = `${BASE_URL}/Register/RegisterCommunity`;
export const ORG_REGISTER_API = `${BASE_URL}/Register/RegisterOrg`;
export const USER_REGISTER_API = `${BASE_URL}/Register/RegisterUser`;

// Shared photo storage — UploadPhoto stores a file to S3 and returns its
// key/url; GetPresignedUrl turns a stored key into a temporary viewable URL
// (the bucket is private). Used by Family and Item, which still have
// photoUrl on their DTOs (unlike Event, which doesn't).
export const UPLOAD_PHOTO_API = `${BASE_URL}/Upload/UploadPhoto`;
export const GET_PRESIGNED_URL_API = `${BASE_URL}/Upload/GetPresignedUrl`;

// Upload Section
export const GET_FAMILIES_API = `${BASE_URL}/cosmaresource/family`;
export const ADD_FAMILY_API = `${BASE_URL}/Family/AddMember`;
export const UPDATE_FAMILY_API = `${BASE_URL}/Family/UpdateMember`;
export const DELETE_FAMILY_API = `${BASE_URL}/Family/DeleteMember`;

export const GET_EVENTS_API = `${BASE_URL}/cosmaresource/event`;
export const CREATE_EVENT_API = `${BASE_URL}/Event/CreateEvent`;
export const UPDATE_EVENT_API = `${BASE_URL}/Event/UpdateEvent`;
export const DELETE_EVENT_API = `${BASE_URL}/Event/DeleteEvent`;

export const GET_ITEMS_API = `${BASE_URL}/cosmaresource/item`;
export const ADD_ITEM_API = `${BASE_URL}/Item/CreateItem`;
export const UPDATE_ITEM_API = `${BASE_URL}/Item/UpdateItem`;
export const DELETE_ITEM_API = `${BASE_URL}/Item/DeleteItem`;

export const GET_REMINDERS_API = `${BASE_URL}/Reminders/GetReminders`;
export const ADD_REMINDER_API = `${BASE_URL}/Reminders/SaveReminder`;
export const DELETE_REMINDER_API = `${BASE_URL}/Reminders/Delete`;

export const GET_FREQUENCY_LIST_API = `${BASE_URL}/Reminders/GetFrequencyList`;
export const GET_LIST_TIME_API = `${BASE_URL}/Reminders/GetListTime`;

// Behavioural is a single daily record per user (sleep/food/mood/activity
// scores), upserted in place — not a list of entries. Same endpoint for both
// create and update; the backend has no separate list/delete/dropdown-option
// endpoints for this resource.
export const GET_BEHAVIOURALS_API = `${BASE_URL}/Behavioural/GetBehaviouralReport`;
export const ADD_BEHAVIOURAL_API = `${BASE_URL}/Behavioural/SaveBehavioural`;

// Behavioural Form: a separate, detailed daily journal (meal times, sleep,
// exercise, hobbies, etc.) living alongside the simple score record above —
// same "Behavioural" domain, different resource. Plain free-text fields, no
// dropdown-option lookups needed.
export const CREATE_BEHAVIOURAL_FORM_API = `${BASE_URL}/Behavioural/CreateBehavioural`;
export const GET_BEHAVIOURAL_FORMS_API = `${BASE_URL}/Behavioural/GetBehaviouralForms`;

// NOTE: no equivalent for this in the dementia backend yet — only schedule.js
// still depends on it (behavioural.js's own use of this was removed since the
// new BehaviouralDto has no time-of-day field at all). Left as-is so
// schedule.js doesn't break; it will 404 until the backend adds a matching
// route, same as the other unimplemented placeholders below.
export const GET_TIME_LIST_API = `${BASE_URL}/BehaviouralUi/GetTimeList`;
export const GET_TASK_LIST_API = `${BASE_URL}/Task/GetTasks`;
export const GET_COLOR_LIST_API = `${BASE_URL}/cosmaresource/colours`;

export const GET_SCHEDULES_API = `${BASE_URL}/Schedule/GetSchedules`;
export const ADD_SCHEDULE_API = `${BASE_URL}/Schedule/CreateSchedule`;
export const UPDATE_SCHEDULE_API = `${BASE_URL}/Schedule/UpdateSchedule`;
export const DELETE_SCHEDULE_API = `${BASE_URL}/Schedule/DeleteSchedule`;

// No Song/UpdateSong endpoint exists on dementia — AddSong is assumed to
// upsert (same pattern as Event/Family/Item, which don't split create/update
// this way for their own Add-only paths either).
export const GET_SONGS_API = `${BASE_URL}/cosmaresource/song`;
export const ADD_SONG_API = `${BASE_URL}/Song/AddSong`;
export const DELETE_SONG_API = `${BASE_URL}/Song/DeleteSong`;

// Profile Section
// CustomerUi controller is gone entirely on dementia — GET_USERNAME_API and
// GET_ORG_API are replaced by a single Profile/GetMyProfile call (see
// redux/actions/profile.js's getProfile). GET_DIAGNOSIS/STAGES/SEX/RELIGION_LIST
// have no equivalent found anywhere in the new API — genuine gap, not a
// rename; getProfile no longer requests them.
export const GET_MY_PROFILE_API = `${BASE_URL}/Profile/GetMyProfile`;

// No replacement found for Profile/GetProfiles (dead code — 0 live callers,
// not migrated) or OtherProfile/GetOtherProfiles (controller gone entirely).
export const GET_PROFILES_API = `${BASE_URL}/Profile/GetProfiles`;
export const GET_OTHER_PROFILES_API = `${BASE_URL}/OtherProfile/GetOtherProfiles`;

export const GET_ACCOUNTS_API = `${BASE_URL}/Account/GetAccounts`;

// Confirmed with the product owner: this client app uses Org/GetDoctors and
// Org/GetPatients everywhere (not the Profile/GetDoctors+GetPatients
// alternates, which serve a different clinician-facing use case).
export const GET_ORGS_API = `${BASE_URL}/Org/GetOrgs`;
export const GET_DOCTORS_API = `${BASE_URL}/Org/GetDoctors`;
export const GET_PATIENTS_API = `${BASE_URL}/Org/GetPatients`;
export const GET_FEEDBACKS_API = `${BASE_URL}/BugReport/GetBugReports`;
export const ADD_FEEDBACK_API = `${BASE_URL}/BugReport/SubmitBugReport`;
export const DELETE_ACCOUNT_API = `${BASE_URL}/Account/DeleteAccount`;

// Dashboard endpoints all moved to different controllers on dementia (not
// just renamed) — PlayReport got split apart into Score/Activity/Summary/
// Behavioural. All confirmed via swagger to still take the same
// DateRangeRequest {userId, fromDate, toDate} body this file already sends,
// except GET_ACTIVITY_SUMMARY_API (ActivityPeriodRequest, adds `period` —
// also already what this file sends).
export const GET_ADVANCE_SEARCH_API = `${BASE_URL}/Search/AdvancedSearch`;
export const GET_REPORTS_API = `${BASE_URL}/Score/GetUserScores`;
export const GET_MODULE_PLAY_REPORTS_API = `${BASE_URL}/PlayReport/GetModulePlayReports`;
export const GET_PLAY_REPORTS_API = `${BASE_URL}/PlayReport/GetPlayReports`;
export const GET_COGNITIVE_FITNESS_API = `${BASE_URL}/CognativeScore/GetCognitiveFitnessScore`;
export const GET_SESSION_SUMMARY_API = `${BASE_URL}/Summary/GetSessionSummary`;
export const GET_BEHAVIOURAL_REPORT_API = `${BASE_URL}/Behavioural/GetBehaviouralReport`;
export const GET_EXERCISE_ACTIVITY_API = `${BASE_URL}/Activity/GetExerciseActivity`;
export const GET_MEDITATION_ACTIVITY_API = `${BASE_URL}/Activity/GetMeditationActivity`;
export const GET_PLAY_REPORT_BY_ID_API = `${BASE_URL}/PlayReport/GetPlayReports`;
export const GET_MODULE_PLAY_REPORT_BY_ID_API = `${BASE_URL}/PlayReport/GetModulePlayReports`;

// Users Section
export const CREATE_ORG_API = `${BASE_URL}/Org/CreateOrg`;
export const CREATE_DOCTOR_API = `${BASE_URL}/Org/CreateDoctor`;
export const CREATE_PATIENT_API = `${BASE_URL}/Org/CreatePatient`;

// Password reset/forgot already goes through Cognito directly
// (utils/cognito.js), not this backend — these were never real endpoints on
// the old backend either and still aren't on dementia. Not a regression.
export const FORGET_PASSWORD_API = `${BASE_URL}/Profile/ResetPasswordRequest`;
export const RESET_PASSWORD_API = `${BASE_URL}/Profile/ResetPassword`;
export const GET_PROFILE_BY_ID_API = `${BASE_URL}/Profile/GetProfileById`;
export const UPDATE_USERINFO_API = `${BASE_URL}/Profile/UpdateProfile`;
export const CHANGE_PASSWORD_API = `${BASE_URL}/Account/ChangePassword`;
// Same Org/GetOrgs and Org/GetDoctors this app uses everywhere else — no
// separate "list" vs "org doctors" endpoints exist on dementia.
export const GET_ORG_LIST_API = `${BASE_URL}/Org/GetOrgs`;
export const GET_ALL_ORG_USER_API = `${BASE_URL}/Profile/GetAllOrgUsers`;
export const GET_ORG_DOCTOR_API = `${BASE_URL}/Org/GetDoctors`;
export const GET_PROFILES_BY_ID_API = `${BASE_URL}/Profile/GetProfileById`;

// Pre-aggregated activity summary
export const GET_ACTIVITY_SUMMARY_API = `${BASE_URL}/Summary/GetActivitySummary`;

// ── EEG Stroke Rehab APIs ─────────────────────────────────────────────────────
export const EEG_PATIENT_API        = (userId) => `${BASE_URL}/eeg/patients/${userId}`;
export const EEG_MRI_API            = (userId) => `${BASE_URL}/eeg/patients/${userId}/mri`;
export const EEG_SESSIONS_API       = (userId) => `${BASE_URL}/eeg/patients/${userId}/sessions`;
export const EEG_SESSION_LATEST_API = (userId) => `${BASE_URL}/eeg/patients/${userId}/sessions/latest`;
export const EEG_DAY1_API           = (userId) => `${BASE_URL}/eeg/patients/${userId}/sessions/day1`;
export const EEG_PROGRESS_API       = (userId) => `${BASE_URL}/eeg/patients/${userId}/progress/summary`;
export const EEG_PROGRESS_ERD_API   = (userId) => `${BASE_URL}/eeg/patients/${userId}/progress/erd`;
export const EEG_PROGRESS_ASYM_API  = (userId) => `${BASE_URL}/eeg/patients/${userId}/progress/asymmetry`;
export const EEG_PROGRESS_EX_API    = (userId) => `${BASE_URL}/eeg/patients/${userId}/progress/exercises`;
export const EEG_PROTOCOL_API       = (userId) => `${BASE_URL}/eeg/patients/${userId}/protocol/current`;
export const EEG_ALERTS_API         = (userId) => `${BASE_URL}/eeg/patients/${userId}/alerts`;
